<script setup>
import StartupPlate from "@/components/trackers/StartupPlate.vue";
import {inject, onMounted, ref, watch} from "vue";
import Draggable from 'vuedraggable'
import {cloneArray} from "@/helpers/array";
import trackersCompaniesService from "@/services/trackers-companies.service";
import constants from "@/constants/constants";
import PulseLoader from "vue-spinner/src/MoonLoader.vue";
import StartupCardPopup from "@/components/trackers/StartupCardPopup"
import Reviews from "@/components/trackers/Reviews.vue";
import AnswersView from "@/components/trackers/AnswersView.vue";
import InviteStartup from "@/components/startups/InviteStartup.vue";
import {trackerTypes} from "@/constants/trackers";
import useStoreTrigger from "@/composables/useStoreTrigger";
import ScrolledBottom from "@/components/ScrolledBottom.vue";
import Loader from "@/components/ui/Loader.vue";
import ReviewEdit from "@/components/trackers/ReviewEdit.vue";

const storeTrigger = useStoreTrigger();
const emit = defineEmits(['reviewUpdated']);

const tracker = inject('tracker').value;
const addCellData = inject("addCellData");
const addStartupsToStage = inject("addStartupsToStage");
const serverFilters = inject("serverFilters");
const companiesListUpdated = inject("companiesListUpdated");
const removeCompany = inject("removeCompany");
const filteredCompaniesCount = inject("filteredCompaniesCount");
const filtersApplied = inject("filtersApplied");
const trackerUpdated = inject("trackerUpdated");

const stagesData = ref([]);
const initStagesData = ref([]);
const header = ref(null);
const startupCardPopup = ref(null);
const editReviewModal = ref(null);
const reviewsModal = ref(null);
const viewAnswersModal = ref(null);
const inviteModal = ref(null);
const reopenDetails = ref(null);
const stickHeader = ref(false);
const loading = ref(false);
const lastLinkId = ref(0);

watch(serverFilters, () => {
    filteredCompaniesCount.value = null;
    getStagesData(true);
});

watch(trackerUpdated, (value) => {
    if (value) {
        getStagesData(true);
    }
});

watch(companiesListUpdated, (value) => {
    if (value) {
        getStagesData(true, false);
        getNewStartups();
    }
});

onMounted(() => {
    getStagesData();
});

async function getStagesData(reload = false, loadStartupsIfEmpty = true) {
    if (reload) {
        stagesData.value = [];
    } else {
        loading.value = true;
    }

    const {data} = await trackersCompaniesService.getStagesData(tracker.id, {filters: serverFilters.value});
    let companiesCount = 0;

    data.forEach(stage => {
        let index = stagesData.value.findIndex(item => item.id === stage.id);

        if (index < 0) {
            stagesData.value.push({
                ...stage,
                startups: [],
                loading: false,
            })

            index = stagesData.value.length - 1;
        }

        stagesData.value[index].count = stage.count;
        companiesCount += stage.count;

        if (reload || (stagesData.value[index].count && !stagesData.value[index].startups.length && loadStartupsIfEmpty)) {
            stagesData.value[index].startups = [];
            getStageStartups(stage.id);
        }
    })

    if (filtersApplied.value) {
        filteredCompaniesCount.value = companiesCount;
    }

    initStagesData.value = cloneArray(stagesData.value);
    loading.value = false;
}

async function getStageStartups(stageId) {
    let index = stagesData.value.findIndex(item => item.id === stageId);

    if (!stagesData.value[index].loading && stagesData.value[index].startups.length < stagesData.value[index].count) {
        stagesData.value[index].loading = true;

        let stageLastLinkId = stagesData.value[index].startups.at(-1)?.link_id || 0;

        const {data} = await trackersCompaniesService.getStageCompanies(tracker.id, stageId, stageLastLinkId, {filters: serverFilters.value});
        stagesData.value[index].startups.push(...data.filter(item => !stagesData.value[index].startups.some(startup => startup.id === item.id)));

        if (stagesData.value[index].startups.length && stagesData.value[index].startups[0].link_id > lastLinkId.value) {
            lastLinkId.value = stagesData.value[index].startups[0].link_id;
        }

        initStagesData.value = cloneArray(stagesData.value);
        stagesData.value[index].loading = false;
    }
}

async function getNewStartups() {
    const {data} = await trackersCompaniesService.getNewCompanies(tracker.id, lastLinkId.value, {filters: serverFilters.value});

    for (let stageId in data) {
        let index = stagesData.value.findIndex(item => item.id === parseInt(stageId));

        if (index >= 0) {
            data[stageId].forEach(item => {
                stagesData.value[index].startups.unshift(item)

                if (item.link_id > lastLinkId.value) {
                    lastLinkId.value = item.link_id
                }
            })
        }
    }

    initStagesData.value = cloneArray(stagesData.value);
}

function loadMore(index) {
    getStageStartups(stagesData.value[index].id);
}

async function onStartupStageChange(e) {
    if ('added' in e) {
        let changes = [];
        let sendEmail = false;

        for (let stage in stagesData.value) {
            let initStage = initStagesData.value[stage]
            let changedCompanies = []

            stagesData.value[stage].startups.forEach(startup => {
                if (!initStage.startups.find(item => item.id === startup.id)) {
                    changedCompanies.push(startup.id)
                    sendEmail = stagesData.value[stage].send_email;
                }
            })

            if (changedCompanies.length) {
                changes.push({
                    id: initStage.id,
                    companies: changedCompanies,
                })
            }
        }

        if (changes.length) {
            if (!sendEmail || confirm("Startup will be notified about stage change. Would you like to proceed?")) {
                loading.value = true;

                const {data: updatedStartups} = await trackersCompaniesService.savePipelineCompanies(tracker.id, {stagesCompanies: changes});

                for (let stageId in updatedStartups) {
                    let stage = stagesData.value.find(item => parseInt(item.id) === parseInt(stageId));

                    for (let startupId in updatedStartups[stageId]) {
                        let startupIndex = stage.startups.findIndex(item => parseInt(item.id) === parseInt(startupId));
                        stage.startups[startupIndex] = updatedStartups[stageId][startupId];
                    }
                }

                getStagesData(true);
                storeTrigger('setNeedUpdateTrackerLogs');
            } else {
                stagesData.value = cloneArray(initStagesData.value);
            }
        }
    }
}

async function getStartupData(startupId) {
    const {data} = await trackersCompaniesService.getStartupData(tracker.id, startupId);

    for (let stage in stagesData.value) {
        let index = stagesData.value[stage].startups.findIndex(item => item.id === parseInt(startupId));

        if (index >= 0) {
            for (let field in data) {
                stagesData.value[stage].startups[index][field] = data[field];
            }

            break;
        }
    }
}

function editReview(startupId, fromModal = false) {
    editReviewModal.value.open(startupId)

    if (fromModal) {
        reopenDetails.value = startupId;
        startupCardPopup.value.close();
    }
}

function reviewsUpdated(startupId) {
    startupCardPopup.value.getData();
    emit('reviewUpdated');

    if (serverFilters.value.ratingFilterState === 'all') {
        getStartupData(startupId);
    } else {
        getStagesData(true);
    }
}

function editReviewClosed() {
    if (reopenDetails.value) {
        startupCardPopup.value.open(reopenDetails.value);
        reopenDetails.value = null;
    }
}

function showReviews(startupId) {
    reviewsModal.value.open(tracker.id, startupId);
}

function showAnswers(startupId) {
    viewAnswersModal.value.open(tracker.id, startupId)
}

function showDetails(startupId) {
    startupCardPopup.value.open(startupId)
}

function inviteStartup(startupId) {
    inviteModal.value.open(startupId);
}

async function togglePinned(stageIndex, startupIndex) {
    stagesData.value[stageIndex].startups[startupIndex].pinned = !stagesData.value[stageIndex].startups[startupIndex].pinned;

    await trackersCompaniesService.setPinned(tracker.id, stagesData.value[stageIndex].startups[startupIndex].id, stagesData.value[stageIndex].startups[startupIndex].pinned);

    stagesData.value[stageIndex].startups = [
        ...stagesData.value[stageIndex].startups.filter(startup => startup.pinned),
        ...stagesData.value[stageIndex].startups.filter(startup => !startup.pinned)
    ];
}
</script>

<template>
    <div
        ref="header"
        class="content-tracker-board"
    >
        <PulseLoader :loading="loading" :color="constants.spinnerColor"/>

        <div class="content-tracker-board__header" :class="{'sticky-header': stickHeader}">
            <header
                v-for="(stage, stageIndex) in stagesData"
                :key="stageIndex"
                class="content-tracker-board__title"
            >
                {{ stage.name }} <span class="startups-cnt">({{ stage.count }})</span>
            </header>
        </div>

        <div ref="right-container" class="content-tracker-board__main-wrapper">
            <div class="content-tracker-board__main">
                <div v-for="(stage, stageIndex) in stagesData" :key="stageIndex"
                     class="content-tracker-board__column" :class="'column-' + stage.id">

                    <Draggable
                        v-model="stagesData[stageIndex].startups"
                        handle=".draggable"
                        group="startups"
                        class="content-tracker-board__content"
                        @change="onStartupStageChange"
                    >
                        <StartupPlate
                            v-for="(startup, index) in stagesData[stageIndex].startups"
                            :key="startup.id"
                            :startup="startup"
                            :draggable="startup.draggable"
                            :addData="addCellData"
                            :hasReviews="tracker.reviews_enabled === 1"
                            @editReview="editReview(startup.id)"
                            @showReviews="showReviews(startup.id)"
                            @showAnswers="showAnswers(startup.id)"
                            @cardClicked="showDetails(startup.id)"
                            @invite="inviteStartup"
                            @togglePinned="togglePinned(stageIndex, index)"
                        />

                        <ScrolledBottom @scrolledBottom = loadMore(stageIndex) />
                        <Loader class="mb-1" v-if="stagesData[stageIndex].loading"/>

                        <a
                            v-if="tracker.type !== trackerTypes.PUBLIC"
                            class="button-oblong"
                            @click="addStartupsToStage(stage.id)"
                        >
                            + Add Startup
                        </a>
                    </Draggable>
                </div>
            </div>
        </div>

        <StartupCardPopup
            ref="startupCardPopup"
            :tracker-id="tracker.id"
            :tracker-type="tracker.type"
            :permissions="tracker.user_permissions"
            @editReview="(startupId) => editReview(startupId, true)"
            @showReviews="showReviews"
            @removeStartup="removeCompany"
        />

        <Reviews
            ref="reviewsModal"
            :tracker-id="tracker.id"
            :has-overall-score="tracker.has_overall_score"
            @reviewSaved="reviewsUpdated"
        />

        <ReviewEdit
            ref="editReviewModal"
            :tracker-id="tracker.id"
            @reviewSaved="reviewsUpdated"
            @closed="editReviewClosed"
        />

        <AnswersView
            ref="viewAnswersModal"
        />

        <InviteStartup
            ref="inviteModal"
            :tracker-id="tracker.id"
        />
    </div>
</template>

<style scoped>

</style>
